import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { get, set } from "nodash"
import { HotspotsLayerFilterTypes } from "./hotspots/constants"
import { LayersSliceState } from "./types"
import {
    activateHotspotLabelsLayerAction,
    activateHotspotOrthoLayerAction,
    activateHotspotOrthoOverlapLayerAction,
    activateHotspotPointLayerAction,
    activateHotspotPolygonAreaLayerAction,
    activateHotspotPolygonLayerAction,
    activateHotspotScanAreaLayerAction,
    activateOrthoScanAreaLayerAction,
    activateRemovedHotspotsLayerAction,
    deactivatedFlightPathAction,
    deactivateHotspotLabelsLayerAction,
    deactivateHotspotOrthoLayerAction,
    deactivateHotspotOrthoOverlapLayerAction,
    deactivateHotspotPointLayerAction,
    deactivateHotspotPolygonAreaLayerAction,
    deactivateHotspotPolygonLayerAction,
    deactivateHotspotScanAreaLayerAction,
    deactivateOrthoScanAreaLayerAction,
    deactivateRemovedHotspotsLayerAction,
    setHotspotOverlapLayer,
} from "features/entity-caching/flights/slice"
import { deactivateFire } from "../control/slice"
import { ID, ImagePreviewType, LayerGroup } from "@nova/constants"
import { createFire } from "features/entity-caching/fires/thunks"
import { deleteLine } from "features/pubsub/entityActions/cubes/line"
import { loadProject } from "../control/thunks"
import { createMergedFlight } from "features/pubsub/entityActions/cubes/mergedFlights"
import { setSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { getLocalStorageValue, setLocalStorageValue } from "tools/store/localStorageRegistry"

const initialShowLayerState = {
    hotspotPoints: {},
    hotspotHeatmap: {},
    hotspotLabels: {},
    hotspotOrthos: {},
    hotspotOrthoOverlaps: {},
    hotspotScanArea: getLocalStorageValue("hotspotScanAreaShowState"),
    orthoScanArea: getLocalStorageValue("orthoScanAreaShowState"),
    hotspotPolygons: {},
    hotspotArea: {},
    flightPath: {},
    firePerimeters: {},
    hotspotHexmap: {},
    removedHotspots: {},
}

const initialState: LayersSliceState = {
    currentFire: "",
    show: initialShowLayerState,
    preview: initialShowLayerState,
    history: {
        flightIds: [] as string[],
        perimeterIds: [] as string[],
        show: {},
    },
    filter: {
        hotspots: HotspotsLayerFilterTypes.ActiveOrOut,
    },
    groups: {} as Record<string, LayerGroup>,
    boundingBoxId: undefined,
    hoveredBoundingBoxId: undefined,
    hoveredTextElementId: undefined,
    activeVideoId: "",
    activeImageIds: {} as Record<string, boolean>,
    imagePreviewContext: undefined,
    hoveredImageId: undefined,
    uploadTimestamps: {} as Record<ID, string>,
}

const updateShowLayerState =
    (layerNames: string[], show: boolean) =>
    (
        state: LayersSliceState,
        action: PayloadAction<{
            id: string
        }>
    ) => {
        const { id } = action.payload

        layerNames.forEach((layerName) => {
            set(state, ["show", layerName, id], show)
            if (show) set(state, ["preview", layerName, id], false)
        })
        state.history.show[state.currentFire] = state.show

        if (layerNames.includes("hotspotScanArea")) {
            setLocalStorageValue("hotspotScanAreaShowState", state.show.hotspotScanArea)
        }
        if (layerNames.includes("orthoScanArea")) {
            setLocalStorageValue("orthoScanAreaShowState", state.show.orthoScanArea)
        }
    }

const setVideoId = createAction<string>(`map/layers/setVideoActive`)
const setActiveImageId = createAction<
    { ids: Record<string, boolean>; type: ImagePreviewType; clickedId?: string } | undefined
>(`map/layers/setActiveImageId`)
const setHoveredImageId = createAction<ID | undefined>(`map/layers/setHoveredImageId`)

const activateAllLayersForProject = createAction<{ flightIds: string[] }>(
    `map/layers/activateAllLayersForProject`
)
const deactivateAllLayersForProject = createAction<{ flightIds: string[] }>(
    `map/layers/deactivateAllLayersForProject`
)

const addUploadTimestamp = createAction<{ flightId: ID; timestamp: string }>(
    `map/laylers/addUploadTimestamp`
)

/** @deprecated */
const setBoundingBoxId = createAction<{ id?: string }>(`map/layers/activateBoundingBox`)
const setHoveredTextElementId = createAction<{ id?: string }>(`map/layers/hoveredTextElement`)

export const slice = createSlice({
    name: "map/layers",
    initialState: { ...initialState },
    reducers: {
        changedHotspotsFilter: (
            state: LayersSliceState,
            action: PayloadAction<HotspotsLayerFilterTypes>
        ) => {
            state.filter.hotspots = action.payload
        },
        activateFirePerimetersLayer: updateShowLayerState(["firePerimeters"], true),
        activateFlightPath: (state, action) => {
            const id = action.payload.flightId
            // to fit generic method payload, we explicitly set the id
            updateShowLayerState(["flightPath"], true)(state, {
                payload: { id },
                type: action.type,
            })
        },
        deactivateFlightPath: (state, action) => {
            const id = action.payload.flightId
            // to fit generic method payload, we explicitly set the id
            updateShowLayerState(["flightPath"], false)(state, {
                payload: { id },
                type: action.type,
            })
        },
        setLayerPreviewState: (
            state,
            action: PayloadAction<{ layerName: string; flightId: string; preview: boolean }>
        ) => {
            const { layerName, flightId, preview } = action.payload
            if (get(state, ["show", layerName, flightId]) && preview) return
            set(state, ["preview", layerName, flightId], preview)
        },
        setHoveredBoundingBoxId: (state, action: { payload: string | undefined }) => {
            state.hoveredBoundingBoxId = action.payload
        },
        clearMapLayersState: () => {
            return { ...initialState }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(activateHotspotPointLayerAction, updateShowLayerState(["hotspotPoints"], true))
            .addCase(setVideoId, (state, action) => {
                state.activeVideoId = action.payload
            })
            .addCase(setSnapshotOpen, (state, action) => {
                if (action.payload) {
                    state.imagePreviewContext = undefined
                    state.activeImageIds = {}
                }
            })
            .addCase(setActiveImageId, (state, action) => {
                if (!action.payload) {
                    state.imagePreviewContext = undefined
                    state.activeImageIds = {}
                    return
                }
                const { ids, type } = action.payload
                state.activeImageIds = ids

                const primaryImageId = action.payload.clickedId ?? Object.keys(ids)[0]
                if (!!ids[primaryImageId]) {
                    state.imagePreviewContext = { id: primaryImageId, type }
                } else {
                    state.imagePreviewContext = undefined
                }
            })
            .addCase(setHoveredImageId, (state, action) => {
                state.hoveredImageId = action.payload
            })
            .addCase(deactivateFire, (state) => {
                state.activeVideoId = ""
            })
            .addCase(
                deactivateHotspotPointLayerAction,
                updateShowLayerState(["hotspotPoints", "hotspotLabels"], false)
            )
            .addCase(activateHotspotOrthoLayerAction, updateShowLayerState(["hotspotOrthos"], true))
            .addCase(
                deactivateHotspotOrthoLayerAction,
                updateShowLayerState(["hotspotOrthos"], false)
            )
            .addCase(
                activateHotspotOrthoOverlapLayerAction,
                updateShowLayerState(["hotspotOrthoOverlaps"], true)
            )
            .addCase(
                deactivateHotspotOrthoOverlapLayerAction,
                updateShowLayerState(["hotspotOrthoOverlaps"], false)
            )
            .addCase(setHotspotOverlapLayer, (state, action) => {
                updateShowLayerState(["hotspotOrthoOverlaps"], action.payload.active)(state, {
                    payload: { id: action.payload.id },
                    type: action.type,
                })
            })
            .addCase(
                activateHotspotPolygonLayerAction,
                updateShowLayerState(["hotspotPolygons"], true)
            )
            .addCase(
                deactivateHotspotPolygonLayerAction,
                updateShowLayerState(["hotspotPolygons"], false)
            )
            .addCase(
                activateHotspotLabelsLayerAction,
                updateShowLayerState(["hotspotLabels", "hotspotPoints"], true)
            )
            .addCase(
                deactivateHotspotLabelsLayerAction,
                updateShowLayerState(["hotspotLabels"], false)
            )
            .addCase(
                activateHotspotScanAreaLayerAction,
                updateShowLayerState(["hotspotScanArea"], true)
            )
            .addCase(
                deactivateHotspotScanAreaLayerAction,
                updateShowLayerState(["hotspotScanArea"], false)
            )
            .addCase(
                activateOrthoScanAreaLayerAction,
                updateShowLayerState(["orthoScanArea"], true)
            )
            .addCase(
                deactivateOrthoScanAreaLayerAction,
                updateShowLayerState(["orthoScanArea"], false)
            )
            .addCase(
                activateHotspotPolygonAreaLayerAction,
                updateShowLayerState(["hotspotArea"], true)
            )
            .addCase(
                deactivateHotspotPolygonAreaLayerAction,
                updateShowLayerState(["hotspotArea"], false)
            )
            .addCase(
                activateRemovedHotspotsLayerAction,
                updateShowLayerState(["removedHotspots"], true)
            )
            .addCase(
                deactivateRemovedHotspotsLayerAction,
                updateShowLayerState(["removedHotspots"], false)
            )
            .addCase(deactivatedFlightPathAction, (state, action) => {
                const { flightId: id } = action.payload
                // our incoming payload is slightly different, so we need to pass in a normalized version of it instead:
                updateShowLayerState(["flightPath"], false)(state, {
                    payload: { id },
                    type: action.type,
                })
            })
            .addCase(activateAllLayersForProject, (state, action) => {
                const layerNames = Object.keys(initialShowLayerState)
                const flightIds = action.payload.flightIds
                flightIds.forEach((id) => {
                    updateShowLayerState(layerNames, true)(state, {
                        payload: { id },
                        type: action.type,
                    })
                })
            })
            .addCase(deactivateAllLayersForProject, (state, action) => {
                const layerNames = Object.keys(initialShowLayerState)
                const flightIds = action.payload.flightIds
                flightIds.forEach((id) => {
                    updateShowLayerState(layerNames, false)(state, {
                        payload: { id },
                        type: action.type,
                    })
                })
            })
            .addCase(setHoveredTextElementId, (state, action) => {
                state.hoveredTextElementId = action.payload.id
            })
            // .addCase(setSelectedTool, (state, action) => {
            //     if (action.payload?.toolName !== ToolName.Line)
            //         state.boundingBoxId = action.payload?.tool.id
            // })
            .addCase(deleteLine, (state) => {
                state.boundingBoxId = undefined
            })
            .addCase(createFire.fulfilled, (state) => {
                state.groups = {}
            })
            .addCase(loadProject.fulfilled, (state, action) => {
                action.payload.infraredFlights.forEach(({ id }) =>
                    set(state, ["show", "hotspotPoints", id], true)
                )
            })
            .addCase(addUploadTimestamp, (state, action) => {
                state.uploadTimestamps[action.payload.flightId] = action.payload.timestamp
            })
            .addCase(createMergedFlight, (state, action) => {
                //keep only hotspot points and scan areas on after flights are merged
                const flightIds = action.payload.context?.flightIds
                const layerNames = Object.keys(initialShowLayerState).filter(
                    (s) => s !== "hotspotScanArea" && s !== "orthoScanArea"
                )
                if (!flightIds) return
                flightIds.forEach((flightId) => {
                    updateShowLayerState(layerNames, false)(state, {
                        payload: { id: flightId },
                        type: action.type,
                    })
                    set(state, ["show", "hotspotPoints", flightId], true)
                })
            })
    },
})

const {
    reducer,
    actions: {
        activateFlightPath,
        deactivateFlightPath,
        setLayerPreviewState,
        setHoveredBoundingBoxId,
        clearMapLayersState,
    },
} = slice

export default reducer
export {
    setVideoId,
    activateFlightPath,
    deactivateFlightPath,
    setLayerPreviewState,
    activateAllLayersForProject,
    deactivateAllLayersForProject,
    setBoundingBoxId,
    setHoveredTextElementId,
    setActiveImageId,
    setHoveredImageId,
    addUploadTimestamp,
    setHoveredBoundingBoxId,
    clearMapLayersState,
}
